import dayjs from 'dayjs'

/**
 * Client-side rules for project files (documents that belong to a project, not
 * to a tradie). These mirror the API so users get an instant, friendly message
 * instead of a round-trip 422 — the API stays the source of truth:
 * StoreProjectFileRequest, the ProjectDocumentFile rule and
 * ProjectFile::normalizeTags() in tradiepass-api.
 */

const MB = 1024 * 1024

/** Per-request cap in the API (StoreProjectFileRequest::MAX_FILES_PER_REQUEST). */
export const PROJECT_FILE_MAX_FILES_PER_REQUEST = 10

/** Tag limits (ProjectFile::MAX_TAGS / MAX_TAG_LENGTH). */
export const PROJECT_FILE_MAX_TAGS = 10
export const PROJECT_FILE_MAX_TAG_LENGTH = 30

/** Max bytes by extension (ProjectDocumentFile / ImageOrPdfFile caps). */
const MAX_BYTES_BY_EXTENSION = {
  pdf: 50 * MB,
  docx: 30 * MB,
  jpg: 30 * MB,
  jpeg: 30 * MB,
  png: 30 * MB,
  csv: 10 * MB,
  xlsx: 10 * MB,
}

/** For an <input type="file"> `accept` attribute. */
export const PROJECT_FILE_ACCEPT = '.pdf,.docx,.jpg,.jpeg,.png,.csv,.xlsx'

export const PROJECT_FILE_TYPES_HINT =
  'PDF, Word (.docx), JPG, PNG, CSV or XLSX'

export function fileExtension(name) {
  const value = typeof name === 'string' ? name : ''
  const dot = value.lastIndexOf('.')
  return dot >= 0 ? value.slice(dot + 1).toLowerCase() : ''
}

/**
 * @param {{ name?: string, size?: number }} file
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function validateProjectFile(file) {
  if (!file || typeof file.name !== 'string' || file.name === '') {
    return { ok: false, message: 'Please choose a file.' }
  }

  const max = MAX_BYTES_BY_EXTENSION[fileExtension(file.name)]
  if (max === undefined) {
    return {
      ok: false,
      message: `Only ${PROJECT_FILE_TYPES_HINT} files are allowed.`,
    }
  }

  if (file.size > max) {
    return {
      ok: false,
      message: `Too large (max ${Math.round(max / MB)} MB for this file type).`,
    }
  }

  if (file.size === 0) {
    return { ok: false, message: 'The file is empty.' }
  }

  return { ok: true }
}

/**
 * Split a picked/dropped list into files that can be uploaded and files that
 * cannot, with a reason for each rejected one.
 *
 * @param {Array<{ name: string, size: number }>} files
 * @returns {{ valid: Array, rejected: Array<{ name: string, message: string }> }}
 */
export function splitValidFiles(files) {
  const valid = []
  const rejected = []

  Array.prototype.forEach.call(files || [], (file) => {
    const result = validateProjectFile(file)
    if (result.ok) {
      valid.push(file)
    } else {
      rejected.push({
        name: (file && file.name) || 'Unnamed file',
        message: result.message,
      })
    }
  })

  return { valid, rejected }
}

export function chunk(items, size) {
  const out = []
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size))
  }
  return out
}

/**
 * Same rules as the API's ProjectFile::normalizeTags(): trim, lowercase,
 * collapse inner whitespace, drop blanks and duplicates, cap each tag's length
 * and the number of tags. "SWMS" and " swms " are one tag.
 *
 * @param {Array<*>} tags
 * @returns {string[]}
 */
export function normalizeTags(tags) {
  const clean = []

  ;(Array.isArray(tags) ? tags : []).forEach((tag) => {
    if (typeof tag !== 'string') return
    const value = tag
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()
      .slice(0, PROJECT_FILE_MAX_TAG_LENGTH)
      .trim()
    if (value && clean.indexOf(value) === -1) clean.push(value)
  })

  return clean.slice(0, PROJECT_FILE_MAX_TAGS)
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

/** The amber badge text: "Expires today" / "tomorrow" / "in 12 days". */
export function expiringBadgeLabel(days) {
  if (days === 0) return 'Expires today'
  if (days === 1) return 'Expires tomorrow'
  return `Expires in ${days} days`
}

/**
 * Expiry in plain words, for the file overview: "Expires in 5 days (15/10/2026)",
 * "Expired 3 days ago (12/09/2026)", "No expiry date". Uses the API's
 * `expiry_status` / `days_until_expiry` (worked out in the project's own
 * timezone) rather than re-deriving them from the browser's clock.
 *
 * An archived file is no longer being kept current, so it gets the date only,
 * with no countdown — same rule as the list.
 *
 * @param {{ expires_at?: string|null, expiry_status?: string|null, days_until_expiry?: number|null }} file
 * @param {{ archived?: boolean }} [options]
 * @returns {string}
 */
export function expiryDescription(file, { archived = false } = {}) {
  if (!file || !file.expires_at) return 'No expiry date'

  const date = dayjs(file.expires_at).format('DD/MM/YYYY')
  const days = file.days_until_expiry

  if (archived) {
    return `${file.expiry_status === 'expired' ? 'Expired' : 'Expires'} ${date}`
  }

  if (file.expiry_status === 'expired' && typeof days === 'number') {
    return `Expired ${plural(Math.abs(days), 'day')} ago (${date})`
  }

  if (file.expiry_status === 'expiring' && typeof days === 'number') {
    if (days === 0) return `Expires today (${date})`
    if (days === 1) return `Expires tomorrow (${date})`
    return `Expires in ${days} days (${date})`
  }

  return `Expires ${date}`
}

export function formatBytes(bytes) {
  const n = Number(bytes)
  if (!isFinite(n) || n <= 0) return '—'
  if (n < 1024) return `${n} B`
  if (n < MB) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / MB).toFixed(1)} MB`
}

/** Short human label for the Type column. */
export function fileTypeLabel(mimeType, fileName) {
  const mime = (mimeType || '').toLowerCase()
  const ext = fileExtension(fileName)

  if (mime.indexOf('pdf') !== -1 || ext === 'pdf') return 'PDF'
  if (ext === 'docx' || mime.indexOf('wordprocessingml') !== -1) return 'Word'
  if (
    mime.indexOf('image/') === 0 ||
    ['jpg', 'jpeg', 'png'].indexOf(ext) !== -1
  ) {
    return 'Image'
  }
  if (
    ['csv', 'xlsx'].indexOf(ext) !== -1 ||
    mime.indexOf('spreadsheet') !== -1
  ) {
    return 'Spreadsheet'
  }
  return ext ? ext.toUpperCase() : 'File'
}
